/**
 * Intrlock Bridge Platform API client.
 *
 * Talks to the lambda-provisioning Lambda (deployed at
 * https://ovle12ewnh.execute-api.us-east-1.amazonaws.com/devices/*).
 *
 * This is separate from the legacy `bridgeApi.ts` — that one talks to the
 * older bridge-command Lambda which targets the old single-purpose
 * intrlock-bridge-agent. The new platform's endpoints live here.
 */

const PROVISIONING_API =
  process.env.REACT_APP_BRIDGE_PROVISIONING_API ||
  'https://ovle12ewnh.execute-api.us-east-1.amazonaws.com';

/**
 * Auth headers for admin endpoints. Prefers the logged-in super_admin's
 * Bearer JWT (same intrlock_token used everywhere else in the admin app) —
 * the bridge-provisioning Lambda verifies it server-side. Falls back to the
 * legacy pre-shared admin token if no JWT is available, so this remains
 * compatible during the cutover.
 */
function adminAuthHeaders(): Record<string, string> {
  const jwt = localStorage.getItem('intrlock_token');
  if (jwt) return { authorization: `Bearer ${jwt}` };
  const legacy = process.env.REACT_APP_BRIDGE_ADMIN_TOKEN;
  return legacy ? { 'x-admin-token': legacy } : {};
}

export interface ActivateBySerialRequest {
  serial: string;
  pin: string;
  customerId: string;
  siteId: string;
  role?: string;
  roleConfig?: Record<string, unknown>;
}

export interface ActivateByTokenRequest {
  /** Base64url-encoded activation token from the QR */
  token: string;
  customerId: string;
  siteId: string;
  role?: string;
  roleConfig?: Record<string, unknown>;
}

export interface ActivationResult {
  ok: boolean;
  serial: string;
  role: string;
  iotEndpoint: string;
}

export interface UpgradeRoleRequest {
  manifestUrl: string;
  action?: 'install' | 'upgrade' | 'uninstall';
  /** Pre-shared admin token (will be replaced by Cognito JWT auth) */
  adminToken: string;
}

export interface UpgradeRoleResult {
  ok: boolean;
  serial: string;
  role: string;
  action: string;
  note?: string;
}

async function postJson<T>(path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const res = await fetch(`${PROVISIONING_API}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...extraHeaders },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { error: text };
  }
  if (!res.ok) {
    const msg =
      (parsed as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return parsed as T;
}

async function postJsonAdmin<T>(path: string, body: unknown): Promise<T> {
  return postJson(path, body, adminAuthHeaders());
}

export interface ActivatedDevice {
  serial: string;
  model: string;
  role: string;
  status: 'activated' | 'unactivated' | 'deactivated';
  customerId?: string;
  siteId?: string;
  registeredAt: string;
  activatedAt?: string;
  roleConfig?: {
    driver?: string;
    channels?: Array<{
      channel: number;
      gpioPin?: number;
      label?: string;
    }>;
    [k: string]: unknown;
  };
  iotEndpoint?: string;
  thingName?: string;
  propertyId?: string | number;
  displayName?: string;
  hasCellular?: boolean;
  cellularUsage?: {
    iface: string;
    monthRxBytes: number;
    monthTxBytes: number;
    todayRxBytes?: number;
    todayTxBytes?: number;
    asOf: string;
  };
  modem?: {
    carrier?: string;
    operatorId?: string;
    accessTech?: string;
    signalQuality?: number;
    state?: string;
    asOf?: string;
  };
  recentEvents?: Array<{
    type: string;
    at: string;
    cmd?: { channel?: number; action?: string; durationMs?: number };
    usage?: { iface: string; rxBytes: number; txBytes: number; totalBytes: number };
    error?: string;
    // reader-specific
    format?: string;
    bitCount?: number;
    facilityCode?: number;
    cardNumber?: number;
    rawHex?: string;
    parityValid?: boolean | null;
    key?: string;
    reader?: string;
    // access-control-panel input fields
    label?: string;
    // BLE scanner fields
    bluetooth_id?: string;
    bleName?: string;
    rssi?: number;
  }>;
}

export const bridgePlatformApi = {
  /** Activate a device using its activation PIN (typed off the sticker) */
  activateBySerial(req: ActivateBySerialRequest): Promise<ActivationResult> {
    return postJson('/devices/activate-by-serial', req);
  },

  /** Activate a device using the QR token (decoded from the QR code) */
  activateByToken(req: ActivateByTokenRequest): Promise<ActivationResult> {
    return postJson('/devices/activate', req);
  },

  /** Push a role upgrade to a device (admin only) */
  upgradeRole(
    serial: string,
    role: string,
    req: UpgradeRoleRequest
  ): Promise<UpgradeRoleResult> {
    return postJsonAdmin(
      `/admin/devices/${encodeURIComponent(serial)}/roles/${encodeURIComponent(role)}/upgrade`,
      req
    );
  },

  /** List devices, optionally filtered by status and/or propertyId.
   *  `_adminToken` retained as legacy second arg for call-site compat; auth is
   *  taken from the logged-in super_admin JWT inside adminAuthHeaders(). */
  async listDevices(
    opts: {
      status?: 'activated' | 'unactivated' | 'deactivated';
      propertyId?: string | number;
    },
    _adminToken?: string
  ): Promise<ActivatedDevice[]> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.propertyId !== undefined && opts.propertyId !== null) {
      params.set('propertyId', String(opts.propertyId));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${PROVISIONING_API}/admin/devices${qs}`, {
      headers: adminAuthHeaders(),
    });
    const text = await res.text();
    let body: unknown;
    try { body = JSON.parse(text); } catch { body = { error: text }; }
    if (!res.ok) {
      throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    return (body as { devices: ActivatedDevice[] }).devices;
  },

  assignDevice(
    serial: string,
    req: {
      propertyId?: string | number | null;
      displayName?: string | null;
      adminToken?: string;
    }
  ): Promise<{ ok: boolean; serial: string }> {
    return postJsonAdmin(
      `/admin/devices/${encodeURIComponent(serial)}/assign`,
      req
    );
  },

  refreshUsage(serial: string, _adminToken?: string): Promise<{ ok: boolean }> {
    return postJsonAdmin(
      `/admin/devices/${encodeURIComponent(serial)}/refresh-usage`,
      {}
    );
  },

  async deleteDevice(serial: string, _adminToken?: string): Promise<{ ok: boolean }> {
    const res = await fetch(
      `${PROVISIONING_API}/admin/devices/${encodeURIComponent(serial)}`,
      { method: 'DELETE', headers: adminAuthHeaders() }
    );
    const text = await res.text();
    let body: unknown;
    try { body = JSON.parse(text); } catch { body = { error: text }; }
    if (!res.ok) {
      throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    return body as { ok: boolean };
  },

  sendRelayCommand(
    serial: string,
    req: {
      channel: number;
      action: 'pulse' | 'on' | 'off' | 'toggle';
      durationMs?: number;
      adminToken?: string;
    }
  ): Promise<{ ok: boolean; serial: string; cmd: unknown }> {
    return postJsonAdmin(
      `/admin/devices/${encodeURIComponent(serial)}/relay/command`,
      req
    );
  },
};

/**
 * Decode an activation token (base64url JSON) into its claims.
 * Useful for previewing what a scanned QR contains before submitting.
 */
export function decodeActivationToken(token: string): {
  serial: string;
  model: string;
  fingerprint: string;
  version: number;
} | null {
  try {
    // base64url → base64
    const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64 + '==='.slice((b64.length + 3) % 4));
    const obj = JSON.parse(json) as {
      s: string;
      m: string;
      f: string;
      v: number;
    };
    if (!obj.s || !obj.m || !obj.f) return null;
    return {
      serial: obj.s,
      model: obj.m,
      fingerprint: obj.f,
      version: obj.v ?? 1,
    };
  } catch {
    return null;
  }
}

/**
 * Extract token from a scanned QR URL of the form
 * `https://activate.intrlock.com/a/<token>` or `https://<host>/a/<token>`.
 * Returns null if the URL doesn't match.
 */
export function tokenFromActivationUrl(url: string): string | null {
  const m = url.match(/\/a\/([A-Za-z0-9_-]+)$/);
  return m ? m[1] : null;
}
